import { useState, useEffect, useMemo } from 'react';
import { NavBar, Image, ImageViewer, Empty, Picker, Button, Loading } from 'antd-mobile';
import { useNavigate } from 'react-router-dom';
import { useTransactions } from '../hooks/useTransactions';
import { ChevronLeft, Calendar, Image as ImageIcon } from 'lucide-react';
import { formatCurrency } from '@tangtung/shared';
import './SummaryPage.css'; // Will overwrite this css file next

const THAI_MONTHS = [
    'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
];

export default function SummaryPage() { // Renamed component
    const navigate = useNavigate();
    const { transactions, refreshTransactions } = useTransactions();
    const [loading, setLoading] = useState(false);
    const [viewerVisible, setViewerVisible] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [pickerVisible, setPickerVisible] = useState(false);

    // Default to current month/year
    const now = new Date();
    const [selectedDate, setSelectedDate] = useState({
        month: now.getMonth(),
        year: now.getFullYear()
    });

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            await refreshTransactions();
            setLoading(false);
        };
        load();
    }, [refreshTransactions]);

    // Parse date helper
    const parseDate = (dateStr: string) => {
        return new Date(dateStr.replace(' ', 'T') + 'Z');
    };

    // Filter transactions with slips
    const slips = useMemo(() => {
        return transactions
            .filter(tx => tx.slip_url) // Must have slip
            .filter(tx => {
                const date = parseDate(tx.created_at);
                return date.getMonth() === selectedDate.month &&
                    date.getFullYear() === selectedDate.year;
            })
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }, [transactions, selectedDate]);

    // Images for viewer
    const viewerImages = slips.map(tx => tx.slip_url as string);

    // Generate picker columns (Years: Current-2 to Current)
    const yearColumns = [];
    for (let i = 0; i < 3; i++) {
        const y = now.getFullYear() - i;
        yearColumns.push({ label: (y + 543).toString(), value: y.toString() });
    }
    const monthColumns = THAI_MONTHS.map((m, i) => ({ label: m, value: i.toString() }));

    const handleImageClick = (index: number) => {
        setCurrentImageIndex(index);
        setViewerVisible(true);
    };

    return (
        <div className="page summary-page">
            {/* Custom Header for Summary Page acting as Album */}
            <div className="slips-header-nav">
                <div className="slips-title">อัลบั้มสลิป</div>
                <Button
                    fill='none'
                    onClick={() => setPickerVisible(true)}
                    className="date-filter-btn-nav"
                >
                    <span className="current-filter-text">
                        {THAI_MONTHS[selectedDate.month]} {selectedDate.year + 543}
                    </span>
                    <Calendar size={18} />
                </Button>
            </div>

            <div className="page-content slips-content">
                {loading ? (
                    <div className="loading-state">
                        <Loading color="primary" />
                        <span>กำลังโหลด...</span>
                    </div>
                ) : slips.length === 0 ? (
                    <div className="empty-state-slips">
                        <div className="empty-icon-bg">
                            <ImageIcon size={48} color="#ccc" />
                        </div>
                        <h3>ไม่มีสลิปในเดือนนี้</h3>
                        <p>สลิปที่ถ่ายไว้จะปรากฏที่นี่</p>
                    </div>
                ) : (
                    <div className="slips-grid">
                        {slips.map((tx, index) => (
                            <div
                                key={tx.id}
                                className="slip-item"
                                onClick={() => handleImageClick(index)}
                            >
                                <Image
                                    src={tx.slip_url || ''}
                                    className="slip-thumbnail"
                                    fit="cover"
                                    lazy
                                />
                                <div className="slip-info-overlay">
                                    <span className="slip-amount">{formatCurrency(tx.amount)}</span>
                                    <span className="slip-date">
                                        {parseDate(tx.created_at).toLocaleDateString('th-TH', { day: 'numeric' })}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <ImageViewer.Multi
                images={viewerImages}
                visible={viewerVisible}
                defaultIndex={currentImageIndex}
                onClose={() => setViewerVisible(false)}
            />

            <Picker
                columns={[
                    yearColumns,
                    monthColumns
                ]}
                visible={pickerVisible}
                onClose={() => setPickerVisible(false)}
                value={[selectedDate.year.toString(), selectedDate.month.toString()]}
                onConfirm={(val) => {
                    const year = val[0];
                    const month = val[1];
                    if (year && month) {
                        setSelectedDate({
                            year: parseInt(year.toString()),
                            month: parseInt(month.toString())
                        });
                    }
                }}
                confirmText="ตกลง"
                cancelText="ยกเลิก"
                title="เลือกเดือนที่ต้องการดู"
            />
        </div>
    );
}
